import classNames from "classnames";
import { InputText } from "primereact/inputtext";
import React, { useState } from "react";

import { getDataTestId } from "../../../utils/getDataTestId";
import { getFsExcludeClassName } from "../../../utils/getFsExcludeClassName";
import { WSInputBaseProps } from "../../types/forms";
import {
  detachFieldProps,
  getFieldAriaProps,
  WSField,
  WSFieldProps
} from "../WSField/WSField";
import {
  WSInputContainer,
  WSInputContainerProps
} from "../WSInputContainer/WSInputContainer";

export type WSInputPasswordProps = WSInputBaseProps &
  Omit<WSInputContainerProps, "align" | "size" | "inputAction"> &
  Omit<WSFieldProps, "action"> & {
    minLength?: number;
    maxLength?: number;
  };

export const WSInputPassword = React.forwardRef<
  HTMLInputElement,
  WSInputPasswordProps
>((props, ref) => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const {
    name,
    status,
    className,
    icon,
    iconRight,
    disabled,
    onChange,
    inputClassName,
    fsExclude,
    ...otherProps
  } = props;

  const { fieldProps, ...inputProps } = detachFieldProps(otherProps);
  const testIdBase = getDataTestId("inputPassword", name, fieldProps.label);
  const ariaProps = getFieldAriaProps({
    name,
    label: fieldProps.label,
    helperText: fieldProps.helperText,
    required: fieldProps.required,
    status,
    message: fieldProps.message
  });

  return (
    <WSField
      {...fieldProps}
      name={name}
      status={status}
      data-testid={testIdBase}
    >
      <WSInputContainer
        status={status}
        icon={icon}
        iconRight={iconRight}
        inputAction={{
          icon: passwordVisible ? "hide" : "view",
          onClick: () => {
            setPasswordVisible(!passwordVisible);
          },
          "data-testid": `${testIdBase}ToggleVisibility`,
          "aria-label": passwordVisible ? "Hide password" : "Show password",
          "aria-pressed": passwordVisible
        }}
        disabled={disabled}
        className={className}
      >
        <InputText
          ref={ref}
          name={name}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value, e)}
          type={passwordVisible ? "text" : "password"}
          className={classNames(
            inputClassName,
            getFsExcludeClassName(fsExclude)
          )}
          data-testid={`${testIdBase}Input`}
          {...ariaProps}
          {...inputProps}
        />
      </WSInputContainer>
    </WSField>
  );
});

WSInputPassword.displayName = "WSInputPassword";
